/**
 * Microscope arithmetic: magnification, field of view, and real size from an image.
 *
 *  - Total magnification = objective × eyepiece × any intermediate or auxiliary lens.
 *  - Field of view at the specimen = field number ÷ (objective × auxiliary lens), with the
 *    field number (the diameter of the field at the intermediate image plane, in mm)
 *    printed on the eyepiece (Nikon MicroscopyU; Microscope World).
 *  - Image size = actual size × magnification (the "IAM" triangle).
 *  - A scale bar gives the scale directly: actual = measured ÷ bar measured × bar value.
 */

export type LengthUnit = "nm" | "um" | "mm" | "cm";

export const MICRONS_PER_UNIT: Record<LengthUnit, number> = {
  nm: 0.001,
  um: 1,
  mm: 1000,
  cm: 10_000,
};

export function toMicrons(value: number, unit: LengthUnit): number {
  return value * MICRONS_PER_UNIT[unit];
}

export function fromMicrons(microns: number, unit: LengthUnit): number {
  return microns / MICRONS_PER_UNIT[unit];
}

/** The unit that writes a length with a number between 1 and 999 where it can. */
export function friendlyUnit(microns: number): LengthUnit {
  const size = Math.abs(microns);
  if (size === 0) {
    return "um";
  }
  if (size < 1) {
    return "nm";
  }
  if (size < 1000) {
    return "um";
  }
  return size < 10_000 ? "mm" : "cm";
}

export type MicroscopeError =
  | "objective"
  | "eyepiece"
  | "auxiliary"
  | "fieldNumber"
  | "image"
  | "actual"
  | "magnification"
  | "bar"
  | "barValue"
  | "feature"
  | "cells";

export function totalMagnification(
  objective: number,
  eyepiece: number,
  auxiliary: number
): number {
  return objective * eyepiece * auxiliary;
}

/** Diameter of the field at the specimen, in mm. */
export function fieldOfViewMm(
  fieldNumber: number,
  objective: number,
  auxiliary: number
): number {
  return fieldNumber / (objective * auxiliary);
}

export function fieldAreaMm2(diameterMm: number): number {
  return Math.PI * (diameterMm / 2) ** 2;
}

export type IamTarget = "actual" | "image" | "magnification";

/** Solve I = A × M for the one unknown; sizes are in microns. */
export function solveIam(
  target: IamTarget,
  actualUm: number | null,
  imageUm: number | null,
  magnification: number | null
): number | null {
  if (target === "actual" && imageUm !== null && magnification !== null) {
    return imageUm / magnification;
  }
  if (target === "image" && actualUm !== null && magnification !== null) {
    return actualUm * magnification;
  }
  if (target === "magnification" && actualUm !== null && imageUm !== null) {
    return imageUm / actualUm;
  }
  return null;
}

export interface ScaleBarResult {
  /** Real size of the feature, in microns. */
  actualUm: number;
  /** Microns represented by one measuring unit (mm on the page, or one pixel). */
  umPerMeasure: number;
  /** Magnification when the bar was measured in a length unit; null for pixels. */
  magnification: number | null;
}

export function fromScaleBar(
  barMeasured: number,
  barUm: number,
  featureMeasured: number,
  barMeasuredUnitMm: number | null
): ScaleBarResult {
  const umPerMeasure = barUm / barMeasured;
  return {
    actualUm: featureMeasured * umPerMeasure,
    umPerMeasure,
    magnification:
      barMeasuredUnitMm === null
        ? null
        : (barMeasured * barMeasuredUnitMm * 1000) / barUm,
  };
}

export function cellSizeUm(fieldOfViewUm: number, cellsAcross: number): number {
  return fieldOfViewUm / cellsAcross;
}

/** Common objectives for the comparison table. */
export const COMMON_OBJECTIVES: readonly number[] = [4, 10, 20, 40, 60, 100];
